import { useAuth0 } from "@auth0/auth0-react";
import { cloneDeep } from "lodash";
import { useEffect } from "react";

import { getAudience, getAudiences } from "@/api";
import { useAudienceContext, useDemo } from "@/context";
import { Audience } from "@/types";
import { defaultBuildAudience, defaultSiteAudience, defaultUploadAudience, demoClients } from "@/utils";

export const useAudience = <T extends Audience["audience_type"]>(
  audienceId: string | undefined,
  audienceType?: T,
  segment?: boolean
): {
  audience: T extends Audience["audience_type"]
    ? Extract<Audience, { audience_type: T }>
    : Audience | null;
  loading: boolean;
  error: string | null;
} => {
  const { state, dispatch } = useAudienceContext();
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {

    const fetchAudience = async (id: string, segment?: boolean) => {
      dispatch({ type: "LOADING" });
      try {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await getAudience(
          accessToken,
          id,
          audienceType ?? "",
          segment
        );

        if (data) {
          if (!audienceType || data.audience_type === audienceType) {
            dispatch({ type: "GET_AUDIENCE", payload: { audience: data } });
          } else {
            dispatch({ type: "ERROR", payload: "Unable to load audience" });
          }
        } else {
          dispatch({ type: "ERROR", payload: error?.message || "" });
        }
      } catch (error: any) {
        dispatch({ type: "ERROR", payload: error.message });
      }
    };

    const currentAudience = state.currentAudience;
    const currentAudienceId = currentAudience?.audience_id;

    // Audience ID Exists - Aka viewing an existing audience
    if (audienceId) {

      // Context already has the right audience
      if (currentAudienceId === audienceId) return;

      // Otherwise fetch from API
      fetchAudience(audienceId, segment);
      return;
    }

    // Audience ID Doesn't Exist - Aka creating a new audience

    // Context already has an active audience of the same type, so do nothing
    if (currentAudience && (!audienceType || currentAudience.audience_type === audienceType)) return;

    // Context is empty or holds a different audience of the same type - create a fresh default audience
    dispatch({
      type: "GET_AUDIENCE",
      payload: {
        audience:
          audienceType === "build"
            ? cloneDeep(defaultBuildAudience)
            : audienceType === "upload"
              ? cloneDeep(defaultUploadAudience)
              : cloneDeep(defaultSiteAudience),
      },
    });
  }, [
    audienceId,
    audienceType,
    getAccessTokenSilently,
    state.currentAudience,
    state.currentAudience?.audience_id,
    state.currentAudience?.audience_type,
    dispatch
  ]);

  return {
    audience: (state.currentAudience ??
      null) as T extends Audience["audience_type"]
      ? Extract<Audience, { audience_type: T }>
      : Audience | null,
    loading: state.loading,
    error: state.error,
  };
};

export const useAudiences = (audienceType?: string) => {
  const { state, dispatch } = useAudienceContext();
  const { getAccessTokenSilently } = useAuth0();

  const client = process.env.REACT_APP_CLIENT || "core";
  const { isDemoMode, demoClient } = useDemo();

  useEffect(() => {
    const fetchAudiences = async () => {
      dispatch({ type: "LOADING" });
      try {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await getAudiences(
          accessToken,
          audienceType ?? "",
          client,
          isDemoMode ? demoClients.find(x => x.name === demoClient)?.label : undefined
        );
        if (data) {
          dispatch({ type: "GET_AUDIENCE_LIST", payload: data });
        } else {
          dispatch({ type: "ERROR", payload: error?.message || "" });
        }
      } catch (error: any) {
        dispatch({ type: "ERROR", payload: error.message });
      }
    };

    fetchAudiences();
    // if (!state.audiences) {
    //   fetchAudiences();
    // }
  }, [audienceType, client, dispatch, getAccessTokenSilently, isDemoMode, demoClient]);

  return {
    audiences: state.audiences,
    loading: state.loading,
    error: state.error,
  };
};
